'use client';

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import apiClient from '@/lib/api/client';
import type { User, LoginRequest, VerifyRequest } from '@/lib/api/types';

type AuthStep = 'login' | 'verify';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  authStep: AuthStep;
  pendingEmail: string | null;
  login: (email: string, password: string) => Promise<void>;
  verify: (code: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  resetAuth: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authStep, setAuthStep] = useState<AuthStep>('login');
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);

  const isAuthenticated = !!user;

  // Функция проверки авторизации
  const checkAuth = useCallback(async () => {
    // Проверяем наличие токена в localStorage
    const token = apiClient.getSessionToken();
    
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      // Восстанавливаем профиль пользователя по сохраненному токену
      const profile = await apiClient.getProfile();
      setUser(profile);
    } catch (error: unknown) {
      const err = error as { status?: number; code?: number };
      // Если токен невалиден (401, 403), он уже удален в apiClient.request()
      // Если это сетевая ошибка, оставляем токен для повторной попытки
      if (err?.status === 401 || err?.status === 403 || err?.code === 401 || err?.code === 403) {
        // Токен истек или невалиден - очищаем состояние
        setUser(null);
        setAuthStep('login');
      }
      // При других ошибках (сеть, таймаут) оставляем токен - может быть временная проблема
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Check session on mount - критически важно для PWA
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Восстановление сессии при возврате в приложение (важно для PWA)
  useEffect(() => {
    const handleVisibilityChange = () => {
      // Когда пользователь возвращается в приложение, проверяем сессию
      if (!document.hidden && !isLoading && !user) {
        const token = apiClient.getSessionToken();
        if (token) {
          // Есть токен, но пользователь не установлен - восстанавливаем
          checkAuth();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Также слушаем событие focus для дополнительной надежности
    const handleFocus = () => {
      if (!isLoading && !user) {
        const token = apiClient.getSessionToken();
        if (token) {
          checkAuth();
        }
      }
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [checkAuth, isLoading, user]);

  const login = useCallback(async (email: string, password: string) => {
    const request: LoginRequest = {
      email,
      password,
      client_id: apiClient.clientId,
    };

    const response = await apiClient.login(request);
    
    setChallengeToken(response.challenge_token);
    setPendingEmail(email);
    setAuthStep('verify');
  }, []);

  const verify = useCallback(async (code: string) => {
    if (!challengeToken || !pendingEmail) {
      throw new Error('No pending login');
    }

    const request: VerifyRequest = {
      email: pendingEmail,
      email_code: code,
      client_id: apiClient.clientId,
      challenge_token: challengeToken,
    };

    await apiClient.verify(request);
    
    // Clear pending state
    setChallengeToken(null);
    setPendingEmail(null);
    setAuthStep('login');
    
    // Get user profile
    const profile = await apiClient.getProfile();
    setUser(profile);
  }, [challengeToken, pendingEmail]);

  const logout = useCallback(async () => {
    try {
      await apiClient.logout();
    } finally {
      setUser(null);
      setAuthStep('login');
      setChallengeToken(null);
      setPendingEmail(null);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const profile = await apiClient.getProfile();
      setUser(profile);
    } catch (error: unknown) {
      const err = error as { status?: number; code?: number };
      // Если ошибка авторизации, очищаем состояние
      if (err?.status === 401 || err?.status === 403 || err?.code === 401 || err?.code === 403) {
        setUser(null);
        setAuthStep('login');
      } else {
        // При других ошибках не очищаем пользователя
        setUser(null);
      }
    }
  }, []);

  const resetAuth = useCallback(() => {
    setAuthStep('login');
    setChallengeToken(null);
    setPendingEmail(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated,
        authStep,
        pendingEmail,
        login,
        verify,
        logout,
        refreshUser,
        resetAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
